import React from 'react';
import { Users, GraduationCap, Heart, School } from 'lucide-react';
import { Avatar } from '../../shared/Avatar';
import { MasteryBar } from '../../shared/MasteryBar';
import { Badge } from '../../../../components/ui/Badge';
import { formatRelativeTime } from '../../../../utils/formatters';
import { ROLE_CONFIG } from './types';
import type { UserRow, RoleFilter } from './types';
export const learnerColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-7475" data-mp="rviyuupi8y9i">
        <Avatar name={v} data-id="element-7476" data-mp="rwax6dahla3h" />
        <div data-id="element-7477" data-mp="cxqlo97fklyh">
          <div className="text-sm font-medium text-gray-900" data-id="element-7478" data-mp="cxz0w7r103cu">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-7479" data-mp="d03c7ar984n5">
            {row.grade ? `Groep ${row.grade}` : '—'}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-7480" data-mp="2cakwuj6j6tx5">{v}</span>
}, {
  key: 'sessionCount',
  label: 'Sessies',
  render: (v: number) => <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-7481" data-mp="bqt0zxhygk9t">
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Beheersing',
  render: (v: number) => <MasteryBar value={v || 0} data-id="element-7482" data-mp="lh5hnemnftao" />
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7483" data-mp="gjvvq8ygxzrl">
        {formatRelativeTime(v, true)}
      </span>
}];
export const teacherColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string, row: UserRow) => <div className="flex items-center gap-2.5" data-id="element-7484" data-mp="1u7uf7qaf13v">
        <Avatar name={v} data-id="element-7485" data-mp="1uzdxk6m18ds" />
        <div data-id="element-7486" data-mp="1s3gdwyoclpv">
          <div className="text-sm font-medium text-gray-900" data-id="element-7487" data-mp="1rv6mdxulwiw">{v}</div>
          <div className="text-xs text-gray-400" data-id="element-7488" data-mp="3twfk4nugb0iz">
            {row.groupName ?? (row.account && 'grade' in row.account ? `Groep ${row.account.grade}` : '—')}
          </div>
        </div>
      </div>
}, {
  key: 'organization',
  label: 'School',
  render: (v: string) => <span className="text-sm text-gray-600" data-id="element-7489" data-mp="5z8573ehm1qo">{v}</span>
}, {
  key: 'linkedCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7490" data-mp="fw34hoil4660">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-7491" data-mp="jwwuqoji1x22" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7492" data-mp="3oalhm4ndubwh">
        {formatRelativeTime(v, true)}
      </span>
}];
export const parentColumns = [{
  key: 'name',
  label: 'Naam',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-7493" data-mp="yao6u57rdqr">
        <Avatar name={v} data-id="element-7494" data-mp="zvm8tqqletd" />
        <span className="text-sm font-medium text-gray-900" data-id="element-7495" data-mp="30l92yp9adbms">{v}</span>
      </div>
}, {
  key: 'linkedCount',
  label: 'Kinderen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7496" data-mp="34krl5d9q38lx">
        <Heart className="w-3.5 h-3.5 text-rose-400" data-id="element-7497" data-mp="2x2jxcifuzesu" />
        {v}
      </span>
}, {
  key: 'lastActive',
  label: 'Laatste activiteit',
  render: (v: number) => <span className="text-xs text-gray-500" data-id="element-7498" data-mp="2o1bzu2l62hzt">
        {formatRelativeTime(v, true)}
      </span>
}];
export const allColumns = [{
  key: 'name',
  label: 'Gebruiker',
  render: (_value: unknown, row: UserRow) => <div className="flex items-center gap-3" data-id="element-7499" data-mp="2e6bq593actuw">
        <Avatar name={row.name} data-id="element-7500" data-mp="29cpn88mxc4ca" />
        <div data-id="element-7501" data-mp="29cfwujxyvv62">
          <div className="font-medium text-gray-900 text-sm" data-id="element-7502" data-mp="29bxq2tdohyjw">{row.name}</div>
          <div className="text-xs text-gray-400" data-id="element-7503" data-mp="24f7eai8wpp4a">{row.email}</div>
        </div>
      </div>
}, {
  key: 'role',
  label: 'Rol',
  render: (_value: unknown, row: UserRow) => <Badge variant={ROLE_CONFIG[row.role].variant} size="sm" data-id="element-7504" data-mp="1vdg718ypxa8b">
        {ROLE_CONFIG[row.role].label}
      </Badge>
}, {
  key: 'organization',
  label: 'Organisatie',
  render: (v: string) => <span className="text-sm text-gray-600 truncate max-w-[140px] block" data-id="element-7505" data-mp="3qhxpw4su1uo4">
        {v}
      </span>
}, {
  key: 'linkedCount',
  label: 'Koppelingen',
  render: (v: number) => <span className="text-sm text-gray-700" data-id="element-7506" data-mp="3gr2rk22bb4mb">{v > 0 ? v : '—'}</span>
}, {
  key: 'xp',
  label: 'XP',
  render: (v: number, row: UserRow) => row.role === 'learner' ? <span className="text-sm font-bold text-violet-600" data-id="element-7507" data-mp="37p3bbbz4g4kf">
          {v.toLocaleString('nl-NL')}
        </span> : <span className="text-xs text-gray-400" data-id="element-7508" data-mp="3bpwy220tvdbq">—</span>
}, {
  key: 'lastActiveFormatted',
  label: 'Actief',
  render: (v: string) => <span className="text-xs text-gray-500" data-id="element-7509" data-mp="32qs315oh624b">{v}</span>
}];
export const schoolColumns = [{
  key: 'name',
  label: 'School',
  render: (v: string) => <div className="flex items-center gap-2.5" data-id="element-7510" data-mp="1106qlikt5bub">
        <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0" data-id="element-7511" data-mp="10yvmfhnga22o">
          <School className="w-4 h-4 text-purple-600" data-id="element-7512" data-mp="2lir0j0z69d2r" />
        </div>
        <span className="text-sm font-medium text-gray-900" data-id="element-7513" data-mp="2gjxq5hf6m26t">{v}</span>
      </div>
}, {
  key: 'learnerCount',
  label: 'Leerlingen',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7514" data-mp="2qhlw73i8mw4a">
        <Users className="w-3.5 h-3.5 text-gray-400" data-id="element-7515" data-mp="348qlpcrw680w" />
        {v}
      </span>
}, {
  key: 'teacherCount',
  label: 'Docenten',
  render: (v: number) => <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700" data-id="element-7516" data-mp="2z9dyus10t0yb">
        <GraduationCap className="w-3.5 h-3.5 text-gray-400" data-id="element-7517" data-mp="3e6543hxdl6vu" />
        {v}
      </span>
}, {
  key: 'avgMastery',
  label: 'Gem. beheersing',
  render: (v: number) => <MasteryBar value={v} data-id="element-7518" data-mp="1jtx5nului7vb" />
}];
export function getColumnsForRole(roleFilter: RoleFilter) {
  switch (roleFilter) {
    case 'learner':
      return learnerColumns;
    case 'teacher':
      return teacherColumns;
    case 'parent':
      return parentColumns;
    case 'school':
      return schoolColumns;
    default:
      return allColumns;
  }
}